import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import { useFormik } from "formik";
import { DECIMALS_SOL } from "helpers/constans";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Col, Input, Label, Row, Spinner } from "reactstrap";
import { getAssetCampaign, getCampaign, updateFundingCampaign } from "store/campaign/thunk";
import { getPoolTokenBalance } from "store/wallet/thunk";
import * as Yup from "yup";

export interface Props {
  isOpen?: boolean;
  campaign: any | null;
  listTokenA: any[];
  listTokenB: any[];
  walletCentral: any | null;
  onClose?: () => void;
  triggerRefresh?: (type?: string) => void;
}

const CampaignFunding = ({ isOpen = false, campaign = null, listTokenA = [], listTokenB = [], walletCentral = null, onClose, triggerRefresh }: Props) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);

  const LIST_TOKEN_A: any = listTokenA || [];
  const LIST_TOKEN_B: any = listTokenB || [];

  const onCloseClick = () => {
    onClose && onClose();
    formik.resetForm();
  };

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading(true);
      const data = {
        id: campaign?.ID,
        listFunding: (values?.listFunding || []).map((item: any) => ({
          address: (item?.amountA !== '' || item?.amountB !== '' || item?.amountNative !== '') ? item?.address : null,
          ...(item?.amountA !== '' && { amountA: Number(Number(item?.amountA || 0).toFixed(Number(item?.decimalsA))) * Math.pow(10, Number(item?.decimalsA)) }),
          ...(item?.amountB !== '' && { amountB: Number(Number(item?.amountB || 0).toFixed(Number(item?.decimalsB))) * Math.pow(10, Number(item?.decimalsB)) }),
          ...(item?.amountNative !== '' && { amountNative: Number(Number(item?.amountNative || 0).toFixed(Number(DECIMALS_SOL))) * Math.pow(10, Number(DECIMALS_SOL)) }),
        })).filter((item: any) => item?.address !== null),
      };
      const response: any = await updateFundingCampaign(data);
      setIsLoading(false);
      if (response?.data) {
        toast(t("The process has been completed."), CONFIG_OPTION_TOAST_NORMAL);
        triggerRefresh && triggerRefresh();
      } else {
        toast(`${response || "Error - Please try again!"}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading(false);
      toast(`${error?.message || "Error - Please try again!"}`, CONFIG_OPTION_TOAST_ERROR);
    }
  };

  const validationSchema = Yup.object({
    listFunding: Yup.array()
      .of(
        Yup.object({
          address: Yup.string().required(t("Required")),
          amountA: Yup.number().min(0, t("Must be positive")).nullable(),
          amountB: Yup.number().min(0, t("Must be positive")).nullable(),
          amountNative: Yup.number().min(0, t("Must be positive")).nullable(),
          balanceA: Yup.number().min(0, t("Must be positive")).nullable(),
          balanceB: Yup.number().min(0, t("Must be positive")).nullable(),
          balanceNative: Yup.number().min(0, t("Must be positive")).nullable(),
        })
      )
      .min(1, t("At least one funding info is required"))
      .required(t("Required")),
    balance: Yup.object({
      walletCentral: Yup.string().nullable(),
      balanceCentral: Yup.string().nullable(),
      walletA: Yup.string().nullable(),
      walletB: Yup.string().nullable(),
      balanceA: Yup.string().nullable(),
      balanceB: Yup.string().nullable(),
      symbolA: Yup.string().nullable(),
      symbolB: Yup.string().nullable(),
      symbolCentral: Yup.string().nullable(),
    }).nullable(),
  });

  const formik = useFormik({
    initialValues: {
      listFunding: [],
      balance: {
        walletCentral: '',
        balanceCentral: '',
        walletA: '',
        walletB: '',
        balanceA: '',
        balanceB: '',
        symbolA: '',
        symbolB: '',
        symbolCentral: 'SOL',
      },
    },
    validationSchema,
    onSubmit: handleSubmit,
  });


  const getCampaignDetail = async (_campaign: any) => {
    setIsLoadingDetail((_prev) => true);
    const params = {
      owner: walletCentral?.pubkey,
      pool: _campaign?.target_pool,
    }
    const [res1, res2, res3]: any = await Promise.all([getCampaign(_campaign?.ID), getAssetCampaign(_campaign?.ID), getPoolTokenBalance(params)]);
    setIsLoadingDetail((_prev) => false);
    if (res1?.data) {
      const data = res1?.data;
      const asset_wallets = res2?.data || [];
      const main_position_config = data?.main_position_config;
      const tokenA = LIST_TOKEN_A?.find((item: any) => item?.value === main_position_config?.token_mint_a) || null;
      const tokenB = LIST_TOKEN_B?.find((item: any) => item?.value === main_position_config?.token_mint_b) || null;
      const decimalsA = tokenA?.decimals || 0;
      const decimalsB = tokenB?.decimals || 0;
      const listFunding = asset_wallets?.map((item: any) => ({
        address: item?.wallet,
        amountA: '',
        amountB: '',
        amountNative: '',
        decimalsA: decimalsA,
        decimalsB: decimalsB,
        symbolA: tokenA?.symbol,
        symbolB: tokenB?.symbol,
        balanceA: Number(item?.balanceA) / Math.pow(10, Number(decimalsA)),
        balanceB: Number(item?.balanceB) / Math.pow(10, Number(decimalsB)),
        balanceNative: Number(item?.balanceNative) / Math.pow(10, Number(DECIMALS_SOL)),
      }));
      const dataForm = {
        listFunding: listFunding,
        balance: {
          walletA: res3?.tokens?.tokenA?.address,
          walletB: res3?.tokens?.tokenB?.address,
          walletCentral: walletCentral?.pubkey,
          balanceA: res3?.tokens?.tokenA?.balance?.uiAmountString,
          balanceB: res3?.tokens?.tokenB?.balance?.uiAmountString,
          balanceCentral: `${Number(res3?.native) / Math.pow(10, Number(DECIMALS_SOL))}`,
          symbolA: tokenA?.symbol,
          symbolB: tokenB?.symbol,
          symbolCentral: 'SOL',
        },
      };
      formik.setValues(dataForm);
    }
  };

  useEffect(() => {
    if (!!campaign?.ID) {
      getCampaignDetail(campaign);
    }
  }, [campaign?.ID]);

  return (
    <div className="py-2 relative" style={{ minHeight: "calc(100dvh - 200px)" }}>
      {isLoadingDetail && (
        <div className="position-absolute z-index-1000" style={{ zIndex: 998, top: "0px", right: "0px", bottom: "0px", left: "0px", backgroundColor: "#cdcdcd4a", display: "flex", alignItems: "start", justifyContent: "center" }}>
          <span style={{ transform: "translateY(40dvh)", zIndex: 999 }}><Spinner className="text-primary" /></span>
        </div>
      )}
      <form onSubmit={formik.handleSubmit}>
        {/* Balance Info Section */}
        <div style={{ border: "1px dashed var(--vz-primary)", borderRadius: "4px", padding: "10px" }} className="position-relative py-3 pt-3 mb-4">
          <span className="text-primary position-absolute fs-13 px-1" style={{ backgroundColor: "var(--vz-secondary-bg)", top: "0px", transform: "translateY(-50%)" }}>
            {t("Balance")}
          </span>
          <div className="d-flex flex-column align-items-start gap-1 ms-0">

            <div className="mb-2 pb-2" style={{ borderBottom: "1px dashed #eaeaea" }}>
              <div className="text-muted fs-13">{t('Wallet Central')}: <span className="text-primary fs-13">{formik.values?.balance?.walletCentral || '-'}</span></div>
              <div className="text-muted fs-13">{t('Balance')}: <span className="text-primary fs-13">{formik.values?.balance?.balanceCentral}</span> <span className="text-muted fs-13">{formik.values?.balance?.symbolCentral || ''}</span></div>
            </div>

            <div className="mb-2 pb-2" style={{ borderBottom: "1px dashed #eaeaea" }}>
              <div className="text-muted fs-13">1. {t('Token A')}: <span className="text-primary fs-13">{formik.values?.balance?.walletA || '-'}</span></div>
              <div className="text-muted fs-13">{t('Balance')}: <span className="text-primary fs-13">{formik.values?.balance?.balanceA}</span> <span className="text-muted fs-13">{formik.values?.balance?.symbolA || ''}</span></div>
            </div>

            <div>
              <div className="text-muted fs-13">2. {t('Token B')}: <span className="text-primary fs-13">{formik.values?.balance?.walletB || '-'}</span></div>
              <div className="text-muted fs-13">{t('Balance')}: <span className="text-primary fs-13">{formik.values?.balance?.balanceB}</span> <span className="text-muted fs-13">{formik.values?.balance?.symbolB || ''}</span></div>
            </div>
          </div>
        </div>
        {/* Funding Info Section */}
        <div style={{ border: "1px dashed var(--vz-primary)", borderRadius: "4px", padding: "10px" }} className="position-relative py-3 pt-4 mb-4">
          <span className="text-primary position-absolute fs-13 px-1" style={{ backgroundColor: "var(--vz-secondary-bg)", top: "0px", transform: "translateY(-50%)" }}>
            {t("Funding")}
          </span>
          {formik.values.listFunding.map((funding: any, index: number) => (
            <div key={index} className="mb-4" style={{ borderBottom: "1px dashed #eaeaea" }}>
              <h5 className="mb-2">{index + 1}. {t(`${t("Wallet")}`)}: <span className="text-primary fs-13">
                {funding?.address || '-'}
              </span></h5>
              <Row>
                <Col sm={12} md={6}>
                  <div className="mb-3">
                    <Label className="text-primary fs-13">{t("Amount A")}</Label>
                    <div className="position-relative">
                      <Input
                        className="no-icon-invalid"
                        type="text"
                        name={`listFunding[${index}].amountA`}
                        value={funding.amountA}
                        placeholder=""
                        onChange={(e) => formik.setFieldValue(`listFunding[${index}].amountA`, e.target.value.replace(/[^0-9.]/g, ""))}
                        onBlur={formik.handleBlur}
                        invalid={!!((formik.touched.listFunding?.[index] as any)?.amountA && (formik.errors.listFunding as any)?.[index]?.amountA)}
                      />
                      <span className="text-muted position-absolute" style={{ top: '9px', right: '9px' }}>{funding?.symbolA || ''}</span>
                    </div>
                    <div className="text-muted fs-11">
                      {t('Balance')}: {funding?.balanceA} {funding?.symbolA || ''}
                    </div>

                    {(formik.touched.listFunding?.[index] as any)?.amountA && (formik.errors.listFunding as any)?.[index]?.amountA && (
                      <div className="text-danger fs-12">{((formik.errors.listFunding as any)[index] as any).amountA}</div>
                    )}
                  </div>
                </Col>
                <Col sm={12} md={6}>
                  <div className="mb-3">
                    <Label className="text-primary fs-13">{t("Amount B")}</Label>
                    <div className="position-relative">
                      <Input
                        className="no-icon-invalid"
                        type="text"
                        name={`listFunding[${index}].amountB`}
                        value={funding.amountB}
                        placeholder=""
                        onChange={(e) => formik.setFieldValue(`listFunding[${index}].amountB`, e.target.value.replace(/[^0-9.]/g, ""))}
                        onBlur={formik.handleBlur}
                        invalid={!!((formik.touched.listFunding?.[index] as any)?.amountB && (formik.errors.listFunding as any)?.[index]?.amountB)}
                      />
                      <span className="text-muted position-absolute" style={{ top: '9px', right: '9px' }}>{funding?.symbolB || ''}</span>
                    </div>
                    <div className="text-muted fs-11">
                      {t('Balance')}: {funding?.balanceB} {funding?.symbolB || ''}
                    </div>
                    {(formik.touched.listFunding?.[index] as any)?.amountB && (formik.errors.listFunding as any)?.[index]?.amountB && (
                      <div className="text-danger fs-12">{((formik.errors.listFunding as any)[index] as any).amountB}</div>
                    )}
                  </div>
                </Col>
                <Col sm={12} md={12}>
                  <div className="mb-3">
                    <Label className="text-primary fs-13">{t("Amount Native")}</Label>
                    <div className="position-relative">
                      <Input
                        className="no-icon-invalid"
                        type="text"
                        name={`listFunding[${index}].amountNative`}
                        value={funding.amountNative}
                        placeholder=""
                        onChange={(e) => formik.setFieldValue(`listFunding[${index}].amountNative`, e.target.value.replace(/[^0-9.]/g, ""))}
                        onBlur={formik.handleBlur}
                        invalid={!!((formik.touched.listFunding?.[index] as any)?.amountNative && (formik.errors.listFunding as any)?.[index]?.amountNative)}
                      />
                      <span className="text-muted position-absolute" style={{ top: '9px', right: '9px' }}>SOL</span>
                    </div>
                    <div className="text-muted fs-11">
                      {t('Balance')}: {funding?.balanceNative} SOL
                    </div>
                    {(formik.touched.listFunding?.[index] as any)?.amountNative && (formik.errors.listFunding as any)?.[index]?.amountNative && (
                      <div className="text-danger fs-12">{((formik.errors.listFunding as any)[index] as any).amountNative}</div>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          ))}
        </div>

        {/* Buttons */}
        <Row>
          <Col sm={12} className="mb-2 mt-3">
            <div className="d-flex gap-3 justify-content-end align-items-center">
              <button
                className="btn btn-secondary fs-14 px-1 d-flex justify-content-center align-items-center bg-[#878a99] hover:bg-[#737582]"
                type="button"
                onClick={onCloseClick}
                disabled={isLoading}
                style={{ width: "105px" }}
              >
                <span className="ms-1">{t("Button Close")}</span>
              </button>
              <button
                className="btn btn-primary fs-14 px-1 d-flex justify-content-center align-items-center bg-[#009ef7] hover:bg-[#0086d2]"
                type="submit"
                disabled={isLoading || formik.values?.listFunding?.length === 0}
                style={{ width: "165px" }}
              >
                {isLoading && <Spinner size="sm" className="me-2" />}
                <span>{t("Funding Campaign")}</span>
              </button>
            </div>
          </Col>
        </Row>
      </form>
    </div>
  );
};

export default CampaignFunding;